import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  EventEmitter,
  Output
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient, SettingsService } from '@delon/theme';
import { NzSelectModeType, NzSelectComponent } from 'ng-zorro-antd/select';
import { Subject } from 'rxjs';
import { UserDefaultFormValue } from 'src/app/shared/types/user-default-settings';

import { ApCateAddConfigService } from '../../inline-add-panel/ap-cate-add-config.service';
import { SelectBaseComponent } from '../select-base';
import { SelectInstantBaseNewComponent } from '../selectInstant-base-new';

@Component({
  selector: 'app-ap-cate-select',
  templateUrl: './ap-cate-select.component.html',
  styles: [
    `
      ::ng-deep .ant-select-item-option-content .special-option {
        text-align: center !important;
      }

      .ap-cate-select-container {
        position: relative;
        width: 100%;
        overflow: visible;
      }

      ::ng-deep .add-new-button {
        color: #000000 !important;
        font-weight: 500 !important;
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 2px !important;
        background-color: transparent !important;
        border: none !important;
      }

      ::ng-deep .add-new-button .anticon {
        color: #000000 !important;
      }

      ::ng-deep
        .dropdownSelect:not(:has(.ant-select-item-option-active))
        .ant-select-item-option:nth-child(2):not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
      ::ng-deep
        .dropdownSelect
        .ant-select-item-option:first-child:last-child:not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SupplierCategorySelectComponent),
      multi: true
    }
  ]
})
export class SupplierCategorySelectComponent
  extends SelectInstantBaseNewComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  @Input() activeOnly = true;
  @Input() mode: NzSelectModeType = 'default';
  @Input() defaultType?: 'suppliercategory' = 'suppliercategory';
  @Input() selectDisabled: boolean = false;
  @Input() tabindex: string | null = null;
  @Input() nzAllowClear: boolean = true;

  @Output() readonly addNewSupplierCategory = new EventEmitter<any>();

  NotShowInReport: boolean = false;
  destroy$ = new Subject<void>();
  private defaultUserSettings?: UserDefaultFormValue;

  apCateConfig: any;
  inlineDefaults: any = null;
  // Store instant data separately to preserve it when dropdown closes

  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private resueTabService: ReuseTabService,
    private el: ElementRef,
    private settingsService: SettingsService,
    private apCateAddConfigService: ApCateAddConfigService
  ) {
    super(http, cdr, router);
    this.valueField = '';
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    this.apCateConfig = this.apCateAddConfigService.getApCateConfig();

    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        const trimmedValue = value.trim();
        this.storedInstantData = trimmedValue;
        // Also store in pendingInstantData for Add New (handled by base class)
        this.pendingInstantData = trimmedValue;
      }
    });

    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.tabindex) {
      const nzSelectElement =
        this.nzSelectComponent.originElement.nativeElement;
      const searchInput = nzSelectElement.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabindex?.toString() || '0');

        // Listen to input events directly
        searchInput.addEventListener('input', (event: any) => {
          if (event.target.value) {
            const trimmedValue = event.target.value.trim();
            this.storedInstantData = trimmedValue;
            // Also store in pendingInstantData for Add New (handled by base class)
            this.pendingInstantData = trimmedValue;
          }
        });
      }
    }
  }

  getUrl(searchValue: string): string {
    let url = `api/suppliercategories/query?$top=50&$skip=0&$orderby=code asc`;
    if (searchValue) {
      // Check if this is a valueField match request (indicated by special prefix)
      const isValueFieldMatch = searchValue.startsWith(
        SelectBaseComponent.VALUEFIELD_MATCH_PREFIX
      );
      const actualSearchValue = isValueFieldMatch
        ? searchValue.substring(
            SelectBaseComponent.VALUEFIELD_MATCH_PREFIX.length
          )
        : searchValue;

      const searchStr = actualSearchValue.replaceAll("'", "''");

      // Check if searchValue can be converted to int (ID)
      const searchValueNum = parseInt(actualSearchValue, 10);
      const isNumericId =
        !isNaN(searchValueNum) &&
        searchValueNum.toString() === actualSearchValue.trim();

      if (isValueFieldMatch) {
        // ValueField match: use eq operator for precise matching based on valueField
        // Ignore prerequisite conditions when matching by valueField
        // This ensures we can find records even if they don't meet other conditions
        if (this.valueField === 'id') {
          // Match by ID exactly
          url = `${url}&$filter=(id eq ${isNumericId ? searchValueNum : actualSearchValue})`;
        } else {
          // Match by the specified valueField exactly (code, name, etc.)
          // Use the valueField if set, otherwise default to 'code'
          const fieldName = this.valueField || 'code';
          url = `${url}&$filter=(${fieldName} eq '${encodeURIComponent(searchStr)}')`;
        }
      } else {
        // Partial match: use contains for search behavior
        if (isNumericId) {
          // Include ID matching: (Id eq <id>) or (code/name contains search)
          url = `${url}&$filter=((id eq ${searchValueNum}) or (contains(code, '${encodeURIComponent(
            searchStr
          )}') or contains(name,'${encodeURIComponent(searchStr)}')))`;
        } else {
          // Original behavior: only search by code/name
          url = `${url}&$filter=(contains(code, '${encodeURIComponent(
            searchStr
          )}') or contains(name,'${encodeURIComponent(searchStr)}'))`;
        }
      }
    }
    return url;
  }

  // set the location want to navigate
  jump(): string {
    return '/ap/category';
  }

  // set the param
  queryParam(): String {
    return 'SupplierCategoryCode';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.resueTabService.curUrl.toLowerCase();

    if (curUrl.includes('report')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }
  override async writeValue(value: any) {
    // Extract ID if value is an object (e.g., from API response)
    let idValue = value;
    if (value && typeof value === 'object' && this.valueField) {
      idValue = value[this.valueField] || value.id;
    }

    this.value = idValue;

    // If use selected item as value
    if (this.value) {
      if (!this.valueField) {
        this.ensureItemPresentList(value);
        this.selected = value;
      } else {
        // When valueField is set, we need to fetch the list to find the matching item
        // Use valueField match to find the record that exactly matches the valueField (ID or Code)
        // Prefix keyword with special marker to indicate valueField match (ignores prerequisite conditions)
        let keyword = `${SelectBaseComponent.VALUEFIELD_MATCH_PREFIX}${idValue}`;
        await this.fetchData(keyword);
        this.selected = this.list.find(x => x[this.valueField] == idValue);
      }
    } else {
      this.selected = null;
      //this.list = [];
    }
  }

  // Override search method to store instant data (base class handles pendingInstantData)
  override search(value: string, maxLength: number = 10): void {
    // Base class handles storing in both storedInstantData and pendingInstantData
    super.search(value, maxLength);
  }

  // Override setKeyword method to store instant data (base class handles pendingInstantData)
  override setKeyword(value: string, maxLength: number = 10): void {
    // Base class handles storing in both storedInstantData and pendingInstantData
    super.setKeyword(value, maxLength);
  }

  // Override the addNew method to show inline panel instead of navigation
  override addNew(): void {
    // Use pendingInstantData which was captured synchronously in base class onAddNewClick
    // This ensures we have the data even if storedInstantData was cleared
    const instantDataToUse = this.pendingInstantData || this.storedInstantData;

    // If we still don't have data, try to capture it
    if (!instantDataToUse) {
      this.captureCurrentSearchValue();
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.pendingInstantData = currentSearchValue;
      }
    }

    // Final fallback - use whatever we have
    const finalInstantData =
      this.pendingInstantData || this.storedInstantData || '';

    // Load options and defaults first, then show panel
    Promise.all([this.loadSelectOptions(), this.fetchInlineDefaults()]).then(
      () => {
        // Merge instant data into initialData as fallback
        if (finalInstantData) {
          this.inlineDefaults = {
            ...this.inlineDefaults,
            code: finalInstantData
          };
        }

        this.showInlineAddPanel = true;
        // Force change detection to ensure the panel is rendered
        this.cdr.detectChanges();

        // Retry mechanism to ensure changeInstantData is called after form is ready
        const trySetInstantData = (retries: number = 10) => {
          if (!finalInstantData) return;

          if (this.inlineAddPanel && this.inlineAddPanel.formGroup) {
            const instantFieldName =
              this.apCateConfig?.fields?.find((f: any) => f.isInstantData)
                ?.name || 'code';
            const control = this.inlineAddPanel.formGroup.get(instantFieldName);
            if (control) {
              this.inlineAddPanel.changeInstantData(finalInstantData);
              return;
            }
          }

          if (retries > 0) {
            setTimeout(() => trySetInstantData(retries - 1), 50);
          }
        };

        // Try immediately and with retries
        setTimeout(() => {
          if (this.inlineAddPanel && finalInstantData) {
            this.inlineAddPanel.changeInstantData(finalInstantData);
          }
          trySetInstantData();
        }, 0);

        if (this.nzSelectComponent) {
          this.nzSelectComponent.setOpenState(false);
        }
      }
    );
  }

  onSupplierCategoryCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the supplier category object in the expected format
      const supplierCategory = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code || event.item.supplierCategoryCode,
        name: event.item[event.selectionConfig.labelField],
        isActive: event.item.isActive,
        isDefault: event.item.isDefault
      };

      // Add the new supplier category to the list and select it
      this.list.unshift(supplierCategory);

      // Close the inline panel first
      this.showInlineAddPanel = false;

      // Set the supplier category object in the form control to trigger supplierCategory.valueChanges
      this.value = supplierCategory.id;
      this.selected = supplierCategory.id;

      // Set the supplier category object directly in the form control
      this.onChange(supplierCategory);
      this.writeValue(supplierCategory);
      // Emit addNewSupplierCategory event for parent component to handle
      this.addNewSupplierCategory.emit(supplierCategory);

      // Clear stored instant data after successful creation
      this.clearStoredInstantData();

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onSupplierCategoryCancelled(): void {
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onSupplierCategoryMoreDetails(): void {
    // Navigate to supplier category details page with all the current form data
    const formData = this.inlineAddPanel?.formGroup?.value;
    if (formData) {
      // Create query params with all form fields
      const queryParams: any = {
        autoPopulate: 1
      };

      // Add all form fields to query params
      if (formData.name) queryParams.SupplierCategoryName = formData.name;
      if (formData.code) queryParams.SupplierCategoryCode = formData.code;
      if (formData.isActive !== undefined)
        queryParams.IsActive = formData.isActive;
      if (formData.isDefault !== undefined)
        queryParams.IsDefault = formData.isDefault;

      queryParams.autoPopulate = 1;

      this.router.navigate(['/ap/category'], {
        queryParams: queryParams
      });
    } else {
      this.router.navigate(['/ap/category'], {
        queryParams: {
          autoPopulate: 1
        }
      });
    }
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes - auto-code component will handle code generation
  }

  private loadSelectOptions(): Promise<void> {
    // For supplier category, we don't need to load additional options
    return Promise.resolve();
  }

  private fetchInlineDefaults(): Promise<void> {
    // Call server to get defaults similar to supplier category/detail new()
    return new Promise(resolve => {
      this.http.get('api/suppliercategories/new').subscribe({
        next: (data: any) => {
          const defaults: any = {};
          if (data?.isActive !== undefined) defaults.isActive = data.isActive;
          if (data?.isDefault !== undefined)
            defaults.isDefault = data.isDefault;
          this.inlineDefaults = defaults;
          resolve();
        },
        error: () => {
          this.inlineDefaults = null;
          resolve();
        }
      });
    });
  }
}
